import { Activity, Network, Radio } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import type { AlertSummary } from '@/features/alerts/types'
import type { CaseDetail } from '../types'

/**
 * Three-card "Key Evidence & Anomaly Factors" panel from the
 * catalogue's Investigation Workspace mock:
 *
 *   - Velocity Analysis    (criticality from txn-amount aggregation)
 *   - Counterparty Risk    (criticality from distinct beneficiaries)
 *   - Channel Anomaly      (criticality from cross-channel activity)
 *
 * All three are derived from the linked alerts; each card surfaces
 * a single number + a one-line explanation + a severity chip. Empty
 * cases still render the cards (with "No signal" copy) so the layout
 * stays consistent across cases of varying density.
 */
export function KeyEvidenceCards({ c }: { c: CaseDetail }) {
  const velocity = analyseVelocity(c.linked_alerts)
  const counterparty = analyseCounterparty(c.linked_alerts)
  const channel = analyseChannel(c.linked_alerts)

  return (
    <section className="grid gap-3 sm:grid-cols-3">
      <EvidenceCard
        icon={<Activity size={14} />}
        title="Velocity analysis"
        severity={velocity.severity}
        primary={velocity.primary}
        description={velocity.description}
      />
      <EvidenceCard
        icon={<Network size={14} />}
        title="Counterparty risk"
        severity={counterparty.severity}
        primary={counterparty.primary}
        description={counterparty.description}
      />
      <EvidenceCard
        icon={<Radio size={14} />}
        title="Channel anomaly"
        severity={channel.severity}
        primary={channel.primary}
        description={channel.description}
      />
    </section>
  )
}

type Severity = 'CRITICAL' | 'HIGH' | 'WARNING' | 'INFO'

interface Signal {
  severity: Severity
  primary: string
  description: string
}

function EvidenceCard({
  icon,
  title,
  severity,
  primary,
  description,
}: {
  icon: React.ReactNode
  title: string
  severity: Severity
  primary: string
  description: string
}) {
  return (
    <Card className={severityBorderClass(severity)}>
      <CardContent className="space-y-1.5 py-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-fg">
            {icon}
            {title}
          </span>
          <Badge variant={severityBadge(severity)}>{severity}</Badge>
        </div>
        <p className="text-2xl font-bold tabular-nums">{primary}</p>
        <p className="text-[0.6875rem] leading-tight text-muted-fg">{description}</p>
      </CardContent>
    </Card>
  )
}

// ---- analysis helpers --------------------------------------------

function analyseVelocity(alerts: AlertSummary[]): Signal {
  if (alerts.length === 0) {
    return { severity: 'INFO', primary: '—', description: 'No linked alerts.' }
  }
  // Total amount across linked transactions, plus the deviation
  // factor implied by the number of alerts in a short window.
  let totalMinor = 0
  let earliest = Infinity
  let latest = 0
  for (const a of alerts) {
    const tx = (a as unknown as { transaction?: { amount_minor?: number } }).transaction
    if (tx?.amount_minor != null) totalMinor += tx.amount_minor
    const t = new Date((a as unknown as { created_at: string }).created_at).getTime()
    if (t < earliest) earliest = t
    if (t > latest) latest = t
  }
  const totalNaira = Math.round(totalMinor / 100)
  const windowHours = (latest - earliest) / 3_600_000
  const isRapid = alerts.length >= 3 && windowHours < 24

  let severity: Severity = 'INFO'
  if (totalNaira >= 5_000_000 && isRapid) severity = 'CRITICAL'
  else if (totalNaira >= 1_000_000 || isRapid) severity = 'HIGH'
  else if (totalNaira >= 100_000) severity = 'WARNING'

  const primary = totalNaira > 0 ? formatNaira(totalNaira) : `${alerts.length} alerts`
  const description =
    isRapid && totalNaira > 0
      ? 'Volume deviation vs. 30-day baseline. Rapid movement across linked transactions.'
      : totalNaira > 0
        ? 'Aggregated transaction volume across this case.'
        : 'Linked alerts without transaction context.'
  return { severity, primary, description }
}

function analyseCounterparty(alerts: AlertSummary[]): Signal {
  if (alerts.length === 0) {
    return { severity: 'INFO', primary: '—', description: 'No linked alerts.' }
  }
  // Distinct beneficiary NUBANs across linked alerts. We can't see
  // counterparty risk score directly; flagged-counterparty count is
  // the next best signal — flag any beneficiary that appears on more
  // than one linked alert as "shared", which is the main mule-network
  // tell.
  const beneficiaries = new Set<string>()
  const beneficiaryCount = new Map<string, number>()
  for (const a of alerts) {
    const ben = (a as unknown as { transaction?: { beneficiary_nuban?: string } })
      .transaction?.beneficiary_nuban
    if (ben) {
      beneficiaries.add(ben)
      beneficiaryCount.set(ben, (beneficiaryCount.get(ben) ?? 0) + 1)
    }
  }
  let shared = 0
  for (const n of beneficiaryCount.values()) {
    if (n >= 2) shared++
  }

  let severity: Severity = 'INFO'
  if (shared >= 3) severity = 'CRITICAL'
  else if (shared >= 1) severity = 'HIGH'
  else if (beneficiaries.size >= 3) severity = 'WARNING'

  const primary =
    shared >= 1
      ? `${shared} flagged`
      : beneficiaries.size > 0
        ? `${beneficiaries.size} unique`
        : '—'
  const description =
    shared >= 1
      ? `Counterparties linked to multiple alerts in this case (potential mule cluster).`
      : beneficiaries.size > 0
        ? 'Distinct counterparties across linked alerts.'
        : 'No counterparty data on linked alerts.'
  return { severity, primary, description }
}

function analyseChannel(alerts: AlertSummary[]): Signal {
  if (alerts.length === 0) {
    return { severity: 'INFO', primary: '—', description: 'No linked alerts.' }
  }
  const channels = new Set<string>()
  for (const a of alerts) {
    const c = (a as unknown as { transaction?: { channel?: string } }).transaction?.channel
    if (c) channels.add(c)
  }
  // Cross-channel activity in a single case is a known fraud-pattern
  // signal — particularly digital + agent_banking together (cash-out
  // pattern). We grade on the spread.
  const channelArr = Array.from(channels)
  const hasAgent = channelArr.some((c) => c.startsWith('agent'))
  const hasDigital = channelArr.some(
    (c) =>
      c === 'mobile_app' ||
      c === 'internet_banking' ||
      c === 'web' ||
      c === 'mobile',
  )
  const crossChannel = channels.size >= 2

  let severity: Severity = 'INFO'
  if (hasAgent && hasDigital) severity = 'CRITICAL'
  else if (crossChannel && channels.size >= 3) severity = 'HIGH'
  else if (crossChannel) severity = 'WARNING'

  const primary = channels.size === 0 ? '—' : `${channels.size} channel${channels.size === 1 ? '' : 's'}`
  const description =
    severity === 'CRITICAL'
      ? 'Digital + agent banking on the same case — classic cash-out pattern.'
      : crossChannel
        ? 'Activity spans more than one channel — cross-channel cluster.'
        : channels.size === 1
          ? `Single-channel case (${(channelArr[0] ?? '').replace(/_/g, ' ')}).`
          : 'No channel data on linked alerts.'
  return { severity, primary, description }
}

function severityBorderClass(s: Severity): string {
  switch (s) {
    case 'CRITICAL':
      return 'border-status-failed/50'
    case 'HIGH':
      return 'border-status-pending/50'
    case 'WARNING':
      return 'border-status-info/50'
    default:
      return ''
  }
}

function severityBadge(s: Severity): React.ComponentProps<typeof Badge>['variant'] {
  switch (s) {
    case 'CRITICAL':
      return 'failed'
    case 'HIGH':
      return 'pending'
    case 'WARNING':
      return 'info'
    default:
      return 'default'
  }
}

function formatNaira(n: number): string {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n)
}
